import React, { useCallback, useEffect, useRef } from 'react'
import { Box, Button, ButtonBase, CircularProgress, IconButton, Link, Tooltip } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Link as RouterLink } from 'react-router-dom'
import { PATH_DASHBOARD } from 'src/routes/paths'
import { Close as CloseIcon } from '@material-ui/icons'
import { useSnackbar } from 'notistack5'
import FileNameText from 'src/components/FileNameText'
import { describeWorkflow } from 'src/services/analysisStart'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import { shortDay } from 'src/views/NewAnalysis/model/candidates'
import HomeCard, { CardMessage, RowSkeletons, listSx, metaSx, rowSx } from './HomeCard'
import { MAX_ROWS, readyCountText, readyTypeLabel } from './model'
import { byCardWidth } from './layout'
import useReadySamples from './useReadySamples'

// ----------------------------------------------------------------------
// Home's "Ready to analyse" card: uploads of the last 30 days with no analysis
// yet, newest first, at most MAX_ROWS of them.
//
//   24419_NCH   Paired-end FASTQ   9 Sep   ×  [Analyse]
//   Analyse all 3   Upload more on Files
//
// "Analyse" opens New analysis with that sample (onOpen([candidate])); the panel
// starts from the last used workflow. "Analyse all N" and "+N more" open it with
// every sample that has finished verifying. "Not for analysis" (×, on hover or
// focus) hides a sample from Home, per user in this browser, with an undo. Files
// are uploaded (and validated) on the Files page only; the card links there.
// ----------------------------------------------------------------------

// Sample, type, uploaded, hide, action. The type gives way where the card is
// narrow: phones, half the page on a small laptop, a third of it until the page is
// wide (see ./layout). Hide sits before the action, so every Analyse button lines up
// with the card's right edge.
const FULL_COLUMNS = 'minmax(0, 1fr) auto auto auto auto'
const COMPACT_COLUMNS = 'minmax(0, 1fr) auto auto auto'
const COLUMNS_SX = byCardWidth({
  gridTemplateColumns: {
    base: { xs: COMPACT_COLUMNS, sm: FULL_COLUMNS, md: COMPACT_COLUMNS, lg: FULL_COLUMNS },
    three: COMPACT_COLUMNS,
  },
})
const TYPE_SX = byCardWidth({ display: { base: { xs: 'none', sm: 'block', md: 'none', lg: 'block' }, three: 'none' } })

const HIDE_BUTTON_SIZE = 26

const ReadyRow = ({ item, lastUsed, onOpenOne, onHide, analyseRef }) => {
  const theme = useTheme()
  return (
    <Box
      data-row
      sx={{
        ...rowSx(theme),
        '& .ready-hide': { opacity: 0, transition: theme.transitions.create('opacity', { duration: 120 }) },
        '&:hover .ready-hide, &:focus-within .ready-hide': { opacity: 1 },
        '@media (hover: none)': { '& .ready-hide': { opacity: 1 } },
      }}
    >
      <FileNameText name={item.key} variant="plain" maxLines={1} />
      <Box sx={{ ...metaSx, ...TYPE_SX }}>{readyTypeLabel(item.candidate?.fileType)}</Box>
      <Box sx={metaSx}>{shortDay(item.uploadedAt)}</Box>
      <Tooltip title="Not for analysis" describeChild>
        <IconButton
          className="ready-hide"
          aria-label={`Not for analysis: ${item.key}`}
          // A click from the keyboard (Enter, Space) has no pointer detail.
          onClick={(event) => onHide(item, event.detail === 0)}
          sx={{
            width: HIDE_BUTTON_SIZE,
            height: HIDE_BUTTON_SIZE,
            p: 0,
            borderRadius: `${theme.shape.borderRadius}px`,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
            '&.Mui-focusVisible': { opacity: 1, outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Box sx={{ justifySelf: 'end', display: 'flex', alignItems: 'center' }}>
        {item.verifying ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              height: 26,
              fontSize: 13,
              color: 'text.secondary',
            }}
          >
            <CircularProgress size={12} thickness={5} sx={{ color: 'text.secondary' }} aria-hidden />
            Verifying
          </Box>
        ) : (
          <Tooltip title={lastUsed} describeChild enterDelay={700}>
            <ResultButton
              ref={analyseRef}
              compact
              aria-label={`Analyse ${item.key}`}
              onClick={() => onOpenOne(item)}
              onKeyDown={(event) => {
                if (event.key === 'Delete' || event.key === 'Backspace') {
                  event.preventDefault()
                  onHide(item, true)
                }
              }}
            >
              Analyse
            </ResultButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

/**
 * The card on data from useReadySamples, which Home reads once and shares with the
 * Suggested card (so a sample hidden here leaves both at once).
 */
export const ReadyCardView = ({ ready, onOpen }) => {
  const theme = useTheme()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { items, openable, loaded, failed, retry, hide, unhide, lastWorkflow } = ready

  const visible = items.slice(0, MAX_ROWS)
  const rest = items.slice(visible.length)
  const lastUsed = lastWorkflow ? `Last used: ${describeWorkflow(lastWorkflow)}` : ''

  const openOne = useCallback((item) => onOpen([item.candidate]), [onOpen])
  const openAll = useCallback(() => {
    if (openable.length) onOpen(openable.map((item) => item.candidate))
  }, [onOpen, openable])

  // ------------------------------------------------------------ hide, with undo
  const analyseRefs = useRef([])
  const moreRef = useRef(null)
  const analyseAllRef = useRef(null)
  const focusAfterHide = useRef(null)

  const hideItem = useCallback(
    (item, fromKeyboard) => {
      const { fileIds } = item
      if (fromKeyboard) focusAfterHide.current = Math.max(0, visible.indexOf(item))
      hide(fileIds)
      enqueueSnackbar(`Hidden ${item.key} from Ready to analyse`, {
        autoHideDuration: 6000,
        action: (key) => (
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              unhide(fileIds)
              closeSnackbar(key)
            }}
            sx={{ fontWeight: 600, minWidth: 0 }}
          >
            Undo
          </Button>
        ),
      })
    },
    [visible, hide, unhide, enqueueSnackbar, closeSnackbar],
  )

  // After a keyboard hide, focus moves to the Analyse button now in its place (or
  // the nearest one before it, or the card's own actions) instead of the page.
  useEffect(() => {
    if (focusAfterHide.current === null) return
    const start = Math.min(focusAfterHide.current, visible.length - 1)
    focusAfterHide.current = null
    let target = null
    for (let index = start; index >= 0 && !target; index -= 1) target = analyseRefs.current[index] || null
    target = target || analyseAllRef.current || moreRef.current
    if (target && typeof target.focus === 'function') target.focus()
  })

  // ------------------------------------------------------------ body
  let body
  if (failed) {
    body = (
      <CardMessage
        title="Could not load your uploads."
        action={
          <ResultButton compact onClick={retry}>
            Try again
          </ResultButton>
        }
      />
    )
  } else if (!loaded) {
    body = <RowSkeletons count={3} />
  } else if (!items.length) {
    body = (
      <CardMessage
        title="Nothing waiting."
        detail={
          <>
            Upload files on the{' '}
            <Link component={RouterLink} to={PATH_DASHBOARD.general.files} underline="hover">
              Files page
            </Link>
            . They appear here once verified.
          </>
        }
      />
    )
  } else {
    const restNames = rest.map((item) => item.key)
    const restTip =
      restNames.length > 12
        ? `${restNames.slice(0, 12).join(', ')} and ${restNames.length - 12} more`
        : restNames.join(', ')
    analyseRefs.current = []
    body = (
      <Box sx={{ ...listSx(theme), ...COLUMNS_SX }}>
        {visible.map((item, index) => (
          <ReadyRow
            key={item.id}
            item={item}
            lastUsed={lastUsed}
            onOpenOne={openOne}
            onHide={hideItem}
            analyseRef={(el) => {
              analyseRefs.current[index] = el
            }}
          />
        ))}
        {rest.length > 0 && (
          <Tooltip title={restTip} describeChild>
            <ButtonBase
              data-row
              ref={moreRef}
              aria-label={`+${rest.length} more`}
              onClick={openAll}
              aria-disabled={!openable.length || undefined}
              sx={{
                ...rowSx(theme, { interactive: true }),
                display: 'flex',
                justifyContent: 'flex-start',
                fontSize: 13,
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}
            >
              +{rest.length} more
            </ButtonBase>
          </Tooltip>
        )}
      </Box>
    )
  }

  // ------------------------------------------------------------ footer
  let footer = null
  if (loaded && !failed && items.length) {
    const uploadMore = (
      <Link
        component={RouterLink}
        to={PATH_DASHBOARD.general.files}
        underline="hover"
        sx={{ fontSize: 13, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
      >
        Upload more on Files
      </Link>
    )
    footer =
      openable.length > 1 ? (
        <>
          <Tooltip title={lastUsed} describeChild enterDelay={700}>
            <ResultButton ref={analyseAllRef} onClick={openAll}>
              Analyse all {openable.length}
            </ResultButton>
          </Tooltip>
          {uploadMore}
        </>
      ) : (
        uploadMore
      )
  }

  return (
    <HomeCard
      title="Ready to analyse"
      titleId="home-ready-title"
      count={loaded && !failed ? readyCountText(items.length) : ''}
      footer={footer}
      data-testid="ready-card"
    >
      {body}
    </HomeCard>
  )
}

// The card with its own data. refreshKey changes after a start, so "Last used" is
// read again.
const ReadyCard = ({ refreshKey, ...other }) => {
  const ready = useReadySamples(refreshKey)
  return <ReadyCardView ready={ready} {...other} />
}

export default ReadyCard
